import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { PrimaryButton, PrimaryOutlineButton } from '@/components/button/button-component';
import { Col } from '@/components/grid/grid-component';
import Modal from '@/components/modal/modal-component';
import Input from '../input/input-component';
import Styled from './redactor-link-style';

export const HoverAction = styled.div`
  position: absolute;
  left: 50%;
  transform: translateY(-50%);
  bottom: 110%;
  background-color: white;
`;

/**
 * Redactor Form
 * @param {object} props the props of the component
 * @param {func} props.onRemove the remove functionality
 * @param {func} props.onSubmit the submit functionality
 * @param {string} props.initLink the default link value
 * @returns {JSX.Element} returns the form.
 */
const RedactorLinkForm = ({ onRemove, onSubmit, initLink }) => {
  const methods = useForm({
    defaultValues: {
      redactorUrl: initLink,
    },
  });

  return (
    <FormProvider {...methods}>
      <Styled.MainRow>
        <Col sm={9}>
          <Input
            name="redactorUrl"
            type="text"
            label="Url"
            cy="redactorUrl"
            placeholder="Enter your link here"
            registerOptions={{
              validate: (v) => {
                if (!v.startsWith('https://') && !v.startsWith('www.')) {
                  return 'Please provide valid url';
                }
              },
            }}
          />
        </Col>
        <Styled.ButtonCol sm={12} style={{}}>
          <PrimaryOutlineButton width="auto" title="Remove" type="button" onClick={onRemove} margin="0 5px 0 0" />
          <PrimaryButton title="CONFIRM" width="auto" type="button" onClick={methods.handleSubmit(onSubmit)} />
        </Styled.ButtonCol>
      </Styled.MainRow>
    </FormProvider>
  );
};

RedactorLinkForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  initLink: PropTypes.string.isRequired,
};

/**
 * @param {object} root0 Object containing props
 * @param {boolean} root0.isOpen Whether the modal is open or not
 * @param {Function} root0.close Function to close the modal
 * @param {Function} root0.onSave Function to save the link
 * @param {Function} root0.onRemove Function to remove the link
 * @param {string} root0.initialLink Initial link value
 * @returns {JSX.Element} The rendered RedactorLinkModal component
 */
export const RedactorLinkModal = ({ isOpen, close, onSave, onRemove, initialLink }) => (
  <Modal bodyStyle={{ padding: 0 }} open={isOpen} showClose close={close} title="Hyperlink" width="50%">
    <RedactorLinkForm onSubmit={(values) => onSave(values.redactorUrl)} onRemove={onRemove} initLink={initialLink} />
  </Modal>
);

RedactorLinkModal.defaultProps = {
  isOpen: false,
  close: undefined,
  onSave: undefined,
  onRemove: undefined,
  initialLink: '',
};

RedactorLinkModal.propTypes = {
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  onSave: PropTypes.func,
  onRemove: PropTypes.func,
  initialLink: PropTypes.string,
};

/**
 * @param {object} props - The root object containing the component props
 * @param {object} props.contentState - The content state object
 * @param {string} props.entityKey - The entity key string
 * @param {Array} props.children - The children array
 * @returns {JSX.Element} - The rendered JSX element
 */
const RedactorLink = (props) => {
  const { contentState, entityKey, children } = props;
  const { url } = contentState.getEntity(entityKey).getData();

  return (
    <Styled.StyledLink
      {...props}
      className="redactor-link"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-tooltip-content={url}
    >
      {children}
    </Styled.StyledLink>
  );
};

RedactorLink.defaultProps = {
  contentState: undefined,
  entityKey: undefined,
  children: undefined,
};

RedactorLink.propTypes = {
  contentState: PropTypes.object,
  entityKey: PropTypes.string,
  children: PropTypes.array,
};

export default RedactorLink;
